import React, { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import MainContent from './components/MainContent';
import RightSidebar from './components/RightSidebar';
import ResultsPage from './components/ResultsPage';
import Login from './components/Login';
import { fetchSimpleUserInfo, fetchUserVehicles } from './services/api';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import SignUp from './components/SignUp';

// Main App component
function AppContent() {
  const [selectedVehicle, setSelectedVehicle] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userPK, setUserPK] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [userName, setUserName] = useState('');
  const [userPhoto, setUserPhoto] = useState('');
  const [currentPage, setCurrentPage] = useState('dashboard'); // 'dashboard' or 'results'
  // Check for existing session on component mount
  useEffect(() => {
    const savedUserPK = localStorage.getItem('userPK');
    const savedAuth = localStorage.getItem('isAuthenticated');
    
    if (savedUserPK && savedAuth === 'true') {
      setUserPK(savedUserPK);
      setIsAuthenticated(true);
    }
  }, []);

  // Fetch user info when userPK changes
  useEffect(() => {
    const getUserInfo = async () => {
      if (userPK) {
        try {
          const data = await fetchSimpleUserInfo(userPK, true);
          setUserName(data.userName);
          setUserPhoto(data.userPhoto);
        } catch (err) {
          console.error('Failed to fetch user info:', err);
        }
      }
    };

    getUserInfo();
  }, [userPK]);

  // Fetch user vehicles and pre-select the first one
  useEffect(() => {
    const fetchAndSelectFirstVehicle = async () => {
      if (userPK && currentPage === 'dashboard') {
        try {
          const data = await fetchUserVehicles(userPK);
          if (data && data.vehicles && data.vehicles.length > 0) {
            // Auto-select the first vehicle if none is currently selected
            if (!selectedVehicle) {
              setSelectedVehicle(data.vehicles[0].ScreenName);
            }
          }
        } catch (err) {
          console.error('Failed to fetch vehicles:', err);
        }
      }
    };

    fetchAndSelectFirstVehicle();
  }, [userPK, currentPage, selectedVehicle]);

  const handleVehicleSelect = (vehicleName) => {
    setSelectedVehicle(vehicleName);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  // Handle successful login
  const handleLoginSuccess = (userPK) => {
    setUserPK(userPK);
    setIsAuthenticated(true);
    
    // Save auth state to localStorage
    localStorage.setItem('userPK', userPK);
    localStorage.setItem('isAuthenticated', 'true');
  };
  
  // Handle logout
  const handleLogout = () => {
    setUserPK(null);
    setIsAuthenticated(false);
    
    // Clear auth state from localStorage
    localStorage.removeItem('userPK');
    localStorage.removeItem('isAuthenticated');
  };

  // Handle navigation to results page
  const handleResultClick = () => {
    setCurrentPage('results');
  };

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/signup" element={<SignUp onSignUpSuccess={() => window.location.href = '/login'} />} />
          <Route path="/login" element={<Login onLoginSuccess={handleLoginSuccess} />} />
          <Route path="*" element={<Login onLoginSuccess={handleLoginSuccess} />} />
        </Routes>
      </Router>
    );
  }

  return (
    <Router>
      <Routes>
        <Route path="/*" element={
          <div className="min-h-screen bg-gray-50 p-0">
            <div className="flex flex-col h-screen">
              {/* Desktop Header with Logos and User Profile - visible on medium and larger screens */}
              <div className="hidden md:flex justify-between items-center bg-white/90 p-3 drop-shadow-[0_4px_4px_rgba(0,0,0,0.1)] relative" style={{ zIndex: 50 }}>
                <div className="flex items-center">
                  <img 
                    src="/DRIVE360.png"
                    alt="DRIVE Logo" 
                    className="h-6 sm:h-14 w-auto"
                  />
                </div>
                <div className="relative" style={{ zIndex: 51 }}>
                  <button 
                    onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                    className="flex items-center gap-4 py-2 px-4 rounded-lg hover:bg-gray-100"
                  >
                    <img 
                      src={userPhoto || 'https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?w=400&h=400&fit=crop'}
                      alt={userName}
                      className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-300 shadow-md"
                    />
                    <div className="flex flex-col items-start">
                      <span className="text-gray-700 font-medium">Hello,</span>
                      <span className="text-gray-700 font-medium">{userName || 'Name Surname'}</span>
                    </div>
                    <svg 
                      className={`w-5 h-5 text-gray-500 ${userDropdownOpen ? 'rotate-180' : ''}`}
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown Menu */}
                  <div 
                    className={`absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 origin-top-right transition-all duration-200 ease-in-out
                               ${userDropdownOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'}`}
                    style={{ 
                      zIndex: 999999
                    }}
                  >
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </div>
              </div>

              {/* Mobile Header - only visible on small screens */}
              <div className="md:hidden flex justify-between items-center bg-white/90 p-2 drop-shadow-[0_4px_4px_rgba(0,0,0,0.1)]">
                <button 
                  onClick={toggleMobileMenu}
                  className="bg-primary text-white p-2 rounded-lg flex items-center justify-center"
                  aria-label="Toggle menu"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={mobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"} />
                  </svg>
                </button>
                <div className="flex items-center">
                  <img 
                    src="/DRIVE360.png"
                    alt="DRIVE Logo" 
                    className="h-10 w-auto"
                  />
                </div>
                <button 
                  onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                  className="relative"
                >
                  <img 
                    src={userPhoto || 'https://images.unsplash.com/photo-1622547748225-3fc4abd2cca0?w=400&h=400&fit=crop'}
                    alt={userName}
                    className="w-14 h-14 rounded-full object-cover ring-2 ring-gray-300 shadow-md"
                  />
                  {/* Mobile Dropdown Menu */}
                  <div 
                    className={`absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-1 origin-top-right transition-all duration-200 ease-in-out
                               ${userDropdownOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 -translate-y-2 scale-95 pointer-events-none'}`}
                    style={{ 
                      zIndex: 999999
                    }}
                  >
                    <div className="px-4 py-2 text-sm text-gray-700 border-b border-gray-100">
                      {userName}
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign Out
                    </button>
                  </div>
                </button>
              </div>

              <div className="flex flex-grow overflow-hidden bg-white">
                {/* Sidebar - fixed width, never stretches */}
                <div className={`${mobileMenuOpen ? 'block' : 'hidden'} md:block w-[300px] h-full flex-shrink-0`}>
                  <Sidebar 
                    selectedVehicle={selectedVehicle} 
                    onVehicleSelect={handleVehicleSelect} 
                    closeMobileMenu={() => setMobileMenuOpen(false)}
                    userPK={userPK}
                    onResultClick={handleResultClick}
                    onLogout={handleLogout}
                    currentPage={currentPage}
                  />
                </div>
                
                {/* Main Content + Right Sidebar as a flex row, centered, with gap */}
                <div className="flex flex-row justify-center items-start flex-1 px-6 py-6 max-w-[1600px] mx-auto w-full">
                  {/* Main Content - fixed max width, no left margin */}
                  <div className="flex-1 min-w-0">
                    {currentPage === 'dashboard' ? (
                      <div className="p-2 sm:p-4 lg:p-5 h-full overflow-auto scrollbar-hide">
                        <MainContent selectedVehicle={selectedVehicle} userPK={userPK} />
                      </div>
                    ) : (
                      <div className="p-2 sm:p-4 lg:p-5 h-full overflow-auto scrollbar-hide">
                        <ResultsPage userPK={userPK} />
                      </div>
                    )}
                  </div>
                  {/* Right Sidebar - aligned with cards, not header */}
                  {currentPage === 'dashboard' && (
                    <div className="hidden xl:block xl:w-[320px] flex-shrink-0 sticky top-0 h-screen overflow-y-auto" style={{ zIndex: 40 }}>
                      <RightSidebar selectedVehicle={selectedVehicle} userPK={userPK} />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        } />
      </Routes>
    </Router>
  );
}

// App component without ThemeProvider
function App() {
  return <AppContent />;
}

export default App;
